<div class="checkout-page">

  <header class="checkout-topbar">
    <button type="button" class="link-back" (click)="backToPlan()">
      <mat-icon>arrow_back</mat-icon>
      <span>Back to {{ plan?.name || 'plan' }}</span>
    </button>
    <span class="secure-pill"><mat-icon>lock</mat-icon> Secure checkout</span>
  </header>

  <!-- Loading -->
  <div class="checkout-shell" *ngIf="state === 'loading'">
    <div class="panel skeleton-panel">
      <div class="sk sk-title"></div>
      <div class="sk sk-line"></div>
      <div class="sk sk-block"></div>
      <div class="sk sk-block short"></div>
      <div class="sk sk-button"></div>
    </div>
    <div class="panel summary skeleton-panel">
      <div class="sk sk-title"></div>
      <div class="sk sk-line"></div>
      <div class="sk sk-line"></div>
      <div class="sk sk-line"></div>
    </div>
  </div>

  <!-- Blocking states -->
  <div class="state-card" *ngIf="state === 'not-provider'">
    <div class="state-icon"><mat-icon>engineering</mat-icon></div>
    <h1>Pro is for service providers</h1>
    <p>You're signed in with a customer account. Set up a provider profile first, then come back to start your free trial.</p>
    <div class="state-actions">
      <button type="button" class="btn-primary" (click)="becomeProvider()">Become a provider</button>
      <button type="button" class="btn-ghost" (click)="switchAccount()">Use a different account</button>
    </div>
  </div>

  <div class="state-card" *ngIf="state === 'already-subscribed'">
    <div class="state-icon success"><mat-icon>verified</mat-icon></div>
    <h1>You're already on {{ plan?.name }}</h1>
    <p *ngIf="subscription?.status === 'trialing' && subscription?.trialEnd">
      Your free trial runs until <strong>{{ subscription?.trialEnd | date:'MMMM d, y' }}</strong>.
    </p>
    <p *ngIf="subscription?.status === 'active' && subscription?.currentPeriodEnd">
      Your plan renews on <strong>{{ subscription?.currentPeriodEnd | date:'MMMM d, y' }}</strong>.
    </p>
    <div class="state-actions">
      <button type="button" class="btn-primary" (click)="goToDashboard()">Go to dashboard</button>
      <button type="button" class="btn-ghost" (click)="goToBilling()">Manage billing</button>
    </div>
  </div>

  <div class="state-card" *ngIf="state === 'unavailable'">
    <div class="state-icon warn"><mat-icon>cloud_off</mat-icon></div>
    <h1>Checkout is temporarily unavailable</h1>
    <p>We couldn't reach our payment provider. Please refresh in a moment, or contact support if this keeps happening.</p>
    <div class="state-actions">
      <button type="button" class="btn-primary" (click)="retry()">Try again</button>
      <button type="button" class="btn-ghost" (click)="backToPlan()">Back to plan</button>
    </div>
  </div>

  <!-- Success -->
  <div class="state-card success-card" *ngIf="state === 'success'">
    <div class="success-check">
      <svg viewBox="0 0 52 52" aria-hidden="true">
        <circle cx="26" cy="26" r="24" fill="none" />
        <path fill="none" d="M15 27 l7 7 l15 -16" />
      </svg>
    </div>
    <h1>Welcome to {{ plan?.name }}!</h1>
    <p *ngIf="trialEndDate">
      Your {{ trialDays }}-day free trial is active. You won't be charged until
      <strong>{{ trialEndDate | date:'MMMM d, y' }}</strong>, and you can cancel any time before then.
    </p>
    <p *ngIf="!trialEndDate">
      Your subscription is active<span *ngIf="nextChargeDate">. Next billing date: <strong>{{ nextChargeDate | date:'MMMM d, y' }}</strong></span>.
    </p>
    <ul class="unlocked">
      <li *ngFor="let f of featureHighlights"><mat-icon>check</mat-icon>{{ f }}</li>
    </ul>
    <div class="state-actions">
      <button type="button" class="btn-primary" (click)="goToDashboard()">Go to my dashboard</button>
      <button type="button" class="btn-ghost" (click)="goToBilling()">Manage billing</button>
    </div>
  </div>

  <!-- Checkout form -->
  <div class="checkout-shell" *ngIf="state === 'ready' && plan">

    <main class="panel form-panel">
      <div class="form-heading">
        <h1>{{ trialEligible ? 'Start your free trial' : 'Subscribe to ' + plan.name }}</h1>
        <p *ngIf="trialEligible">{{ trialDays }} days of full {{ plan.name }} access. No charge today — cancel anytime.</p>
        <p *ngIf="!trialEligible">You've already used your free trial, so billing starts today.</p>
      </div>

      <!-- Step 1: account -->
      <section class="step">
        <div class="step-label"><span class="step-num">1</span> Account</div>
        <div class="account-row">
          <div class="avatar">{{ (user?.firstName || user?.username || '?').charAt(0) | uppercase }}</div>
          <div class="account-meta">
            <div class="account-name">{{ user?.firstName }} {{ user?.lastName }}</div>
            <div class="account-email">{{ user?.email || user?.username }}</div>
          </div>
          <button type="button" class="link-btn" (click)="switchAccount()">Not you?</button>
        </div>
      </section>

      <!-- Step 2: payment -->
      <section class="step">
        <div class="step-label"><span class="step-num">2</span> Payment method</div>

        <div class="card-options" *ngIf="savedCards.length">
          <label class="card-option" *ngFor="let card of savedCards" [class.selected]="selectedCardId === card.id">
            <input type="radio" name="card" [checked]="selectedCardId === card.id" (change)="selectCard(card.id)">
            <span class="radio-dot"></span>
            <span class="brand-chip" [attr.data-brand]="card.brand">{{ brandLabel(card.brand) }}</span>
            <span class="card-digits">•••• {{ card.lastFourDigits }}</span>
            <span class="card-exp">Exp {{ card.expiryMonth | number:'2.0' }}/{{ card.expiryYear % 100 }}</span>
          </label>
          <label class="card-option" [class.selected]="selectedCardId === 'new'">
            <input type="radio" name="card" [checked]="selectedCardId === 'new'" (change)="selectCard('new')">
            <span class="radio-dot"></span>
            <mat-icon class="add-icon">add_card</mat-icon>
            <span class="card-digits">Use a new card</span>
          </label>
        </div>

        <div class="element-wrap" [hidden]="selectedCardId !== 'new'">
          <div class="element-skeleton" *ngIf="!elementReady">
            <div class="sk sk-block"></div>
            <div class="sk-row"><div class="sk sk-block"></div><div class="sk sk-block"></div></div>
          </div>
          <div #paymentElementRef class="payment-element" [class.visible]="elementReady"></div>
        </div>
      </section>

      <div class="error-banner" *ngIf="errorMessage" role="alert">
        <mat-icon>error_outline</mat-icon>
        <span>{{ errorMessage }}</span>
      </div>

      <button type="button" class="btn-primary btn-submit" (click)="submit()" [disabled]="!canSubmit">
        <span class="spinner" *ngIf="submitting"></span>
        {{ submitLabel }}
      </button>

      <p class="fine-print" *ngIf="trialEligible">
        By starting your trial you authorize Already Done to charge your card
        ${{ priceAmount }}/month starting {{ trialEndsOn | date:'MMM d, y' }} unless you cancel before then.
        Cancel anytime from Billing.
      </p>
      <p class="fine-print" *ngIf="!trialEligible">
        You'll be charged ${{ priceAmount }} today and every month after until you cancel.
      </p>

      <div class="powered">
        <mat-icon>lock</mat-icon>
        Payments are encrypted and processed by <strong>Stripe</strong>. We never store your card number.
      </div>
    </main>

    <aside class="panel summary">
      <div class="summary-head">
        <div>
          <div class="summary-eyebrow">Your plan</div>
          <div class="summary-plan">{{ plan.name }}</div>
        </div>
        <span class="badge" *ngIf="plan.badge">{{ plan.badge }}</span>
      </div>
      <p class="summary-tagline">{{ plan.tagline }}</p>

      <ul class="summary-features">
        <li *ngFor="let f of featureHighlights"><mat-icon>check_circle</mat-icon>{{ f }}</li>
      </ul>

      <div class="summary-lines">
        <div class="line">
          <span>{{ plan.name }} plan</span>
          <span>${{ priceAmount | number:'1.2-2' }}/mo</span>
        </div>
        <div class="line accent" *ngIf="trialEligible">
          <span>{{ trialDays }}-day free trial</span>
          <span>−${{ priceAmount | number:'1.2-2' }}</span>
        </div>
        <div class="line total">
          <span>Due today</span>
          <span>${{ (trialEligible ? 0 : priceAmount) | number:'1.2-2' }}</span>
        </div>
        <div class="line muted" *ngIf="trialEligible">
          <span>Starting {{ trialEndsOn | date:'MMM d, y' }}</span>
          <span>${{ priceAmount | number:'1.2-2' }}/mo</span>
        </div>
      </div>

      <div class="timeline" *ngIf="trialEligible">
        <div class="tl-item">
          <span class="tl-dot active"></span>
          <div><strong>Today</strong><span>Full {{ plan.name }} access unlocked</span></div>
        </div>
        <div class="tl-item">
          <span class="tl-dot"></span>
          <div><strong>Before it ends</strong><span>You'll get a reminder email</span></div>
        </div>
        <div class="tl-item">
          <span class="tl-dot"></span>
          <div><strong>{{ trialEndsOn | date:'MMM d' }}</strong><span>First charge of ${{ priceAmount }} — cancel before to pay nothing</span></div>
        </div>
      </div>

      <div class="assurances">
        <div><mat-icon>event_busy</mat-icon> Cancel anytime</div>
        <div><mat-icon>shield</mat-icon> Secure payments</div>
      </div>
    </aside>
  </div>
</div>
